import { Group } from '@mantine/core';
import cn from 'classnames';
import * as React from 'react';
import { NavLink, NavLinkRenderProps, useLocation } from 'react-router-dom';

import Text from 'components/shared/Text';
import { useLkLayoutStore } from 'stores/local/layout/LkLayoutStore';
import { useDevices } from 'utils/devices';

import s from './MenuItem.module.scss';

export type NavLinkConfig = {
  label: string;
  Icon: React.ElementType;
  to: string;
};

export type MenuItemProps = NavLinkConfig & {
  collapsed?: boolean;
  activePaths?: string[];
};

const getClassName = ({ isActive }: NavLinkRenderProps) => {
  return cn(s.root, isActive && s.root_active);
};

const isPathActive = (pathname: string, path: string) => pathname === path || pathname.startsWith(`${path}/`);

export const MenuItem: React.FC<MenuItemProps> = ({ label, Icon, to, collapsed = false, activePaths }) => {
  const location = useLocation();
  const { isPhone } = useDevices();
  const { menu } = useLkLayoutStore();

  const clickHandler = React.useCallback(() => {
    if (isPhone) {
      menu.close();
    }
  }, [isPhone, menu]);

  const getLinkClassName = React.useCallback(
    ({ isActive }: NavLinkRenderProps) => {
      const active = activePaths ? activePaths.some((path) => isPathActive(location.pathname, path)) : isActive;

      return cn(s.root, active && s.root_active);
    },
    [activePaths, location.pathname],
  );

  return (
    <NavLink
      className={activePaths ? getLinkClassName : getClassName}
      to={to}
      onClick={clickHandler}
      end={!activePaths}
    >
      <Group gap={8} wrap="nowrap">
        <Icon size={20} />
        {!collapsed && (
          <Text size="xs" fw={600}>
            {label}
          </Text>
        )}
      </Group>
    </NavLink>
  );
};

export default React.memo(MenuItem);
